import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Filter, User, LogOut, LayoutGrid, Megaphone, UserCircle, Phone, Mail } from "lucide-react";
import logoWhite from "@/assets/bizaclia-logo-white.webp.asset.json";
import { AdminGlobalSearch } from "@/components/admin/AdminGlobalSearch";
import { useAnunciosFilters } from "@/contexts/anuncios-filters";
import { countActiveFilters } from "@/components/admin/AnunciosFiltersSheet";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AuthDialog } from "@/components/site/AuthDialog";
import { LoginSuccessOverlay } from "@/components/site/LoginSuccessOverlay";
import { getOnboardingStatus } from "@/lib/onboarding.functions";




export function VipHeader() {
  const router = useRouter();
  const isAdminRoute = useRouterState({
    select: (state) => state.location.pathname.startsWith("/admin"),
  });
  const isPortalVipRoute = useRouterState({
    select: (state) => state.location.pathname.startsWith("/portal-vip"),
  });
  const isAnunciosRoute = useRouterState({
    select: (state) =>
      state.location.pathname === "/admin/anuncios" ||
      state.location.pathname.startsWith("/admin/anuncios/"),
  });
  const { open: filtersOpen, setOpen: setFiltersOpen, filters } = useAnunciosFilters();
  const activeFilters = countActiveFilters(filters);

  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);

  useEffect(() => {
    const handleLoginComplete = () => setShowLoginSuccess(true);

    const checkOnboarding = async () => {
      try {
        const { completed } = await getOnboardingStatus();
        if (!completed) {
          setForceOnboarding(true);
          setAuthOpen(true);
        } else {
          setForceOnboarding(false);
        }
      } catch {
        // silencioso: si falla, no bloqueamos la UI
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      setUserEmail(data.session?.user.email ?? null);
      if (data.session?.user) {
        checkOnboarding();
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUserEmail(session?.user.email ?? null);
      if (session?.user) {
        checkOnboarding();
      } else {
        setForceOnboarding(false);
      }
    });
    window.addEventListener("bizaclia:login-complete", handleLoginComplete);
    return () => {
      window.removeEventListener("bizaclia:login-complete", handleLoginComplete);
      sub.subscription.unsubscribe();
    };
  }, []);

  const displayName = userEmail
    ? userEmail
        .split("@")[0]
        .split(/[._-]+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : null;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    router.navigate({ to: "/" });
  };



  return (
    <header className="sticky top-0 z-40 w-full bg-primary border-b border-white/15 relative overflow-hidden shadow-lg">
      {/* Decorative White Geometric Stroke Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Right-side angular shapes */}
        <svg className="absolute top-0 right-0 h-full w-1/2 text-white/10" fill="none" viewBox="0 0 400 120" preserveAspectRatio="none">
          <path d="M400 0 L150 0 L300 120 L400 120 Z" fill="currentColor" />
          <path d="M100 0 L120 0 L270 120 L250 120 Z" fill="currentColor" fillOpacity="0.5" />
        </svg>
        {/* Fine stroke lines and curves */}
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line x1="-50" y1="20" x2="300" y2="120" stroke="white" strokeWidth="1" strokeOpacity="0.2" />
          <line x1="1200" y1="-20" x2="1500" y2="80" stroke="white" strokeWidth="2" strokeOpacity="0.15" />
          <circle cx="1400" cy="20" r="80" stroke="white" strokeWidth="1" strokeOpacity="0.1" />
          <path d="M-20 40 Q 150 10, 400 50 T 800 30" stroke="white" strokeWidth="1" strokeOpacity="0.2" />
        </svg>
      </div>

      <div className="container mx-auto max-w-7xl flex items-center justify-between h-16 px-4 sm:px-6 relative">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center group" aria-label="Bizaclia.com">
            <img src={logoWhite.url} alt="Bizaclia.com" width={140} height={28} className="h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/30 px-2.5 py-1 rounded-md shadow-sm">
            <Crown className="w-3.5 h-3.5 text-white" />
            <span className="text-[11px] font-bold text-white uppercase tracking-wider">VIP</span>
          </div>
        </div>

        {isAdminRoute && (
          <div className="hidden md:flex flex-1 items-center justify-center px-6 gap-3">
            {isAnunciosRoute && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="gap-1.5 border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                <Filter className="h-4 w-4" />
                Filtros
                {activeFilters > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px]">
                    {activeFilters}
                  </Badge>
                )}
              </Button>
            )}
            <AdminGlobalSearch />
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="hidden sm:inline-flex text-sm font-semibold text-white hover:bg-white/15 hover:text-white"
            asChild
          >
            <Link to="/mybizaclia" search={{ tab: "vip", wizard: true }}>
              Publicar anuncio
            </Link>
          </Button>
          {displayName ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="rounded-full px-4 h-10 gap-2 text-sm font-semibold text-white hover:bg-white/15 hover:text-white border-2 border-white/60 shadow-sm shadow-black/10"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-primary">
                    <User className="h-4 w-4" />
                  </span>
                  <span className="truncate max-w-[140px]">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem asChild>
                  <Link to="/mybizaclia" className="cursor-pointer">
                    <LayoutGrid className="mr-2 h-4 w-4" /> My Bizaclia
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/mybizaclia"
                    search={{ tab: "anuncios" }}
                    className="cursor-pointer"
                    onClick={() => window.dispatchEvent(new CustomEvent("bizaclia:reset-mis-anuncios"))}
                  >
                    <Megaphone className="mr-2 h-4 w-4" /> Mis Anuncios
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mybizaclia" search={{ tab: "cuenta" }} className="cursor-pointer">
                    <UserCircle className="mr-2 h-4 w-4" /> Mi cuenta
                  </Link>
                </DropdownMenuItem>
                {isPortalVipRoute && (
                  <DropdownMenuItem asChild>
                    <Link to="/" className="cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" /> Salir de Bizaclia VIP
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                  Atención al cliente
                </DropdownMenuLabel>
                <a
                  href="tel:900433065"
                  className="flex items-center px-2 py-1.5 text-sm hover:bg-accent rounded-sm mx-1"
                >
                  <Phone className="mr-2 h-4 w-4" /> 900 433 065
                </a>
                <a
                  href="mailto:info@bizaclia.com"
                  className="flex items-center px-2 py-1.5 text-sm hover:bg-accent rounded-sm mx-1"
                >
                  <Mail className="mr-2 h-4 w-4" /> info@bizaclia.com
                </a>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => setAuthOpen(true)}
              className="rounded-full px-5 h-10 bg-white text-primary hover:bg-white/90 text-sm font-semibold"
            >
              Acceder
            </Button>
          )}
        </div>
      </div>
      <AuthDialog
        open={authOpen}
        onOpenChange={(next) => {
          setAuthOpen(next);
          if (!next) setForceOnboarding(false);
        }}
        initialStep={forceOnboarding ? "onboarding-usage" : undefined}
      />
      <LoginSuccessOverlay
        show={showLoginSuccess}
        onDone={() => setShowLoginSuccess(false)}
      />
    </header>
  );
}
